/**
 * IndexedDB layer via idb: a `saves` store (key 'current') plus a ring of
 * 5 timestamped backups written every 10th autosave.
 */
import { openDB, type IDBPDatabase } from 'idb';
import { validateSave, type SaveV1 } from './schema';
import { migrateSave } from './migrate';

const DB_NAME = 'tg-world';
const BACKUP_RING = 5;

let dbPromise: Promise<IDBPDatabase> | null = null;

function db(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, 1, {
    upgrade(d) {
      d.createObjectStore('saves');
      d.createObjectStore('backups');
    },
  });
  return dbPromise;
}

let writesSinceBackup = 0;
let writeInFlight = false;

export async function putSave(save: SaveV1): Promise<void> {
  if (writeInFlight) return; // coalesce — autosave will come around again
  writeInFlight = true;
  try {
    const d = await db();
    await d.put('saves', save, 'current');
    if (++writesSinceBackup >= 10) {
      writesSinceBackup = 0;
      await d.put('backups', save, String(save.savedAt));
      const keys = (await d.getAllKeys('backups')).sort();
      while (keys.length > BACKUP_RING) {
        await d.delete('backups', keys.shift() as IDBValidKey);
      }
    }
  } catch {
    // storage failures must never take the game down; LS snapshot still runs
  } finally {
    writeInFlight = false;
  }
}

/** current -> newest backup -> null. Migrates old versions on the way out. */
export async function loadBestSave(): Promise<SaveV1 | null> {
  try {
    const d = await db();
    const current = migrateSave(await d.get('saves', 'current'));
    if (current) return current;
    const keys = (await d.getAllKeys('backups')).sort().reverse();
    for (const key of keys) {
      const backup = migrateSave(await d.get('backups', key));
      if (backup) return backup;
    }
  } catch {
    // fall through to localStorage reconstruction
  }
  return null;
}

export async function wipeSaves(): Promise<void> {
  const d = await db();
  await d.clear('saves');
  await d.clear('backups');
}

export { validateSave };
